import { Box, Flex, ScrollArea, Text } from '@mantine/core'
import Image from 'next/image'
import Link from 'next/link'
import React, { useState, useEffect } from 'react'
import { useLocaleContext } from '../context/locale'
import { useTranslations } from 'next-intl'

const PlatformSection = ({ platforms, title, color, id, type, country }) => {
  const t = useTranslations('StreamingAvailability.platformSection')

  if (platforms.length === 0) return null

  return (
    <Flex
      align="center"
      justify="flex-start"
      style={{ width: '100%', position: 'relative', gap: '5px' }}
    >
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#0c161e',
          color: '#fff',
          width: '30px',
          height: '80px',
          position: 'relative',
          marginRight: '20px'
        }}
      >
        <Text
          style={{
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
            fontSize: '12px'
          }}
        >
          {t(title)}
        </Text>
        <Box
          style={{
            position: 'absolute',
            right: 0,
            width: '2px',
            height: '100%',
            backgroundColor: color
          }}
        />
      </Box>
      <ScrollArea
        type="auto"
        offsetScrollbars={false}
        scrollbarSize={0}
        style={{ flex: 1 }}
      >
        <Flex
          align="center"
          gap="12px"
          wrap="wrap" // Importante para que los elementos se vayan abajo si no hay espacio
          justify="flex-start"
        >
          {platforms.map((platform) => (
            <Link
              href={`https://click.bb-media.com/redirect?tag=StreamDiscover&url=${encodeURIComponent(
                platform.web_link
              )}&id=${id}&type=${type}&country=${country}`}
              key={platform.name_slug}
              passHref
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image
                src={platform.logo || '/public/placeholder.png'}
                alt={platform.name}
                width={50}
                height={50}
                priority
                style={{
                  borderRadius: '5px',
                  backgroundColor: '#2e2e2e'
                }}
              />
            </Link>
          ))}
        </Flex>
      </ScrollArea>
    </Flex>
  )
}

const StreamingAvailability = ({
  id,
  type,
  title,
  availabilityData,
  selectedCountry
}) => {
  const { platforms } = useLocaleContext()
  const [countryName, setCountryName] = useState(null)

  useEffect(() => {
    const countries = require('../public/countries.json')
    const countryData = countries.find(
      (c) => c.code === (selectedCountry || 'US').toUpperCase()
    )
    setCountryName(
      countryData ? countryData.name : selectedCountry || 'United States'
    )
  }, [selectedCountry])

  const platformsByType = (type) =>
    Array.isArray(availabilityData?.availability)
      ? availabilityData.availability.filter((platform) => platform.type === type)
      : []

  const subscriptionVodPlatforms = platformsByType('subscription-vod')
  const transactionVodPlatforms = platformsByType('transaction-vod')
  const freeVodPlatforms = platformsByType('free-vod')

  const matchedPreferredPlatforms =
    Array.isArray(platforms) && platforms.length > 0
      ? subscriptionVodPlatforms.filter((platform) =>
          platforms.some((preferredPlatform) =>
            platform.name.toLowerCase().includes(preferredPlatform.toLowerCase())
          )
        )
      : []

  if (
    subscriptionVodPlatforms.length === 0 &&
    transactionVodPlatforms.length === 0 &&
    freeVodPlatforms.length === 0 &&
    matchedPreferredPlatforms.length === 0
  ) {
    return (
      <Flex
        direction="column"
        align="center"
        style={{
          width: '100%',
          backgroundColor: '#0E1922',
          gap: '10px',
          padding: '10px',
          borderBottom: '2px solid #7B39B6',
          borderRadius: '4px',
          minHeight: '100px'
        }}
      >
        <Flex
          align="center"
          justify="flex-start"
          style={{
            width: '100%',
            height: '100%',
            minHeight: 'inherit',
            paddingLeft: '20px'
          }}
        >
          <Text color="dimmed" size="sm">
            {countryName && `${title} is not available yet in ${countryName}`}
          </Text>
        </Flex>
      </Flex>
    )
  }

  return (
    <Flex
      direction="column"
      align="center"
      style={{
        width: '100%',
        backgroundColor: '#0E1922',
        gap: '10px',
        padding: '10px',
        borderBottom: '2px solid #7B39B6',
        borderRadius: '4px',
        minHeight: '100px'
      }}
    >
      <PlatformSection
        platforms={matchedPreferredPlatforms}
        title={'yourServices'}
        color="#7b2cbf"
        id={id}
        type={type}
        country={selectedCountry}
      />
      <PlatformSection
        platforms={subscriptionVodPlatforms}
        title={'subscription'}
        color="#7b2cbf"
        id={id}
        type={type}
        country={selectedCountry}
      />
      <PlatformSection
        platforms={transactionVodPlatforms}
        title={'transaction'}
        color="#3a86ff"
        id={id}
        type={type}
        country={selectedCountry}
      />
      <PlatformSection
        platforms={freeVodPlatforms}
        title={'free'}
        color="#6c757d"
        id={id}
        type={type}
        country={selectedCountry}
      />
    </Flex>
  )
}

export default StreamingAvailability
